<template>
  <RevModalBase
    backdrop-background-color="hi"
    background-color="mid"
    class="!max-h-[calc(100dvh-32px)] rounded-t-lg md:!max-h-dvh md:top-1/2 md:bottom-auto md:left-1/2 md:w-640 md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-b-lg"
    :close-button-label="i18n(translations.modalCloseA11y)"
    has-padding
    :name="modalName"
    :title="
      view === 'country'
        ? i18n(translations.switchCountryTitle)
        : i18n(translations.modalTitle)
    "
    @close="resetView"
  >
    <template v-if="view === 'country'" #beforeTitle>
      <RevButtonIcon
        :aria-label="i18n(translations.switchCountryBackA11y)"
        class="absolute left-0 top-0"
        :icon="IconArrowLeft"
        variant="ghost"
        @click="resetView"
      />
    </template>

    <template #body>
      <PostalCodeForm
        v-if="view === 'postal-code'"
        @error="handlePostalCodeError"
        @submit="handlePostalCodeSubmit"
        @switch-country="view = 'country'"
      />
      <CountrySwitchMenuSearchList v-else />
    </template>
  </RevModalBase>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevModalBase, closeModal } from '@ds/components/ModalBase'
import { IconArrowLeft } from '@ds/icons/IconArrowLeft'

import CountrySwitchMenuSearchList from '~/scopes/navigation/components/CountrySwitch/CountrySwitchMenuSearchList.vue'

import translations from './DeliveryPostalCodeModal.translations'
import PostalCodeForm from './PostalCodeForm/PostalCodeForm.vue'

const props = withDefaults(
  defineProps<{
    entryPoint: 'pdp' | 'cart' | 'nav'
    trackingContext?: 'web' | 'mweb' | 'recap'
  }>(),
  {
    trackingContext: undefined,
  },
)

const emit = defineEmits<{
  (e: 'updated'): void
}>()

const i18n = useI18n()
const { closeAllToasts, openSuccessToast } = useTheToast()
const { trackFormSubmit } = useTracking()

const view = ref<'postal-code' | 'country'>('postal-code')

const modalName = computed(() =>
  props.trackingContext
    ? `postalcode_delivery_promise_${props.entryPoint}_${props.trackingContext}`
    : `postalcode_delivery_promise_${props.entryPoint}`,
)

function handlePostalCodeSubmit() {
  closeModal(modalName.value)
  emit('updated')

  trackFormSubmit({
    zone: 'postalcode_modal',
    entryPoint: props.entryPoint,
    status: 'success',
  })
  closeAllToasts()
  openSuccessToast({
    title: i18n(translations.successTitle),
    content: i18n(translations.successDescription),
    duration: 3000,
  })
}

function handlePostalCodeError() {
  trackFormSubmit({
    zone: 'postalcode_modal',
    entryPoint: props.entryPoint,
    status: 'error',
    outcome: 'invalid_format',
  })
}

function resetView() {
  view.value = 'postal-code'
}
</script>
